import { motion } from "framer-motion";
import { Cake, Heart, Baby, PartyPopper, CalendarHeart, Gift, ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const OCCASIONS = [
  { id: "anniversaire", icon: Cake, label: "Anniversaire" },
  { id: "mariage", icon: Heart, label: "Mariage" },
  { id: "naissance", icon: Baby, label: "Naissance" },
  { id: "fetes", icon: PartyPopper, label: "Fêtes & célébrations" },
  { id: "evenements", icon: CalendarHeart, label: "Événements spéciaux" },
  { id: "souvenir", icon: Gift, label: "Souvenirs & déclarations" },
];

export default function GiftIdeas() {
  return (
    <section id="cadeau" className="relative py-20 md:py-28">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/4 top-1/3 h-[350px] w-[350px] rounded-full bg-fuchsia-600/10 blur-[120px]" />
        <div className="absolute right-1/4 bottom-0 h-[300px] w-[300px] rounded-full bg-amber-500/10 blur-[110px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-10 text-center md:mb-14"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-400">05 — Le cadeau idéal</p>
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Une pièce unique, <span className="text-holo">pour chaque événement</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#a09bb5] sm:text-base">
            Carte customisée ou tableau signature 20×20 : offrez bien plus qu'un cadeau — une
            œuvre qui raconte une histoire et marque la journée pour de bon.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:gap-5">
          {OCCASIONS.map((o, i) => (
            <motion.div
              key={o.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, ease: EASE, delay: (i % 3) * 0.09 }}
              data-testid={`gift-occasion-${o.id}`}
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#100e19] px-5 py-5 transition-all duration-300 hover:-translate-y-1 hover:border-fuchsia-400/40 sm:px-6"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-tr from-fuchsia-500/15 to-cyan-500/15 transition-transform duration-300 group-hover:scale-110">
                <o.icon size={20} className="text-amber-300" />
              </span>
              <span className="text-sm font-semibold text-white sm:text-base">{o.label}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
          className="mt-10 text-center"
        >
          <button
            type="button"
            data-testid="gift-cta-offrir"
            onClick={() => scrollToSection("contact")}
            className="btn-holo group inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-bold text-[#08070d]"
          >
            Offrir un cadeau unique
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#6c6685]">
            Carte cadeau possible — mentionnez l'occasion dans votre demande
          </p>
        </motion.div>
      </div>
    </section>
  );
}
